import { ReactNode, useEffect, useRef } from "react";
import { PluginMisconfigured } from "@pexip-engage-public/plugin-events";
import { json, LinksFunction, LoaderFunctionArgs, SerializeFrom } from "@remix-run/node";
import { useLoaderData, useRouteLoaderData } from "@remix-run/react";

import { AppErrorBoundary } from "~/components/AppErrorBoundary";
import { Media } from "~/components/Media";
import { StepOutlet } from "~/components/Step/Step";
import { ContentTitle } from "~/components/Summary/ContentTitle";
import { DesktopSummaryContainer } from "~/components/Summary/DesktopSummaryContainer";
import { MobileSummaryContainer } from "~/components/Summary/MobileSummaryContainer";
import { useActiveStep } from "~/hooks/useActiveStep";
import { sendMessage } from "~/hooks/useSendMessage";
import { GoBackProvider, useBrowserNavigationInterceptor } from "~/modules/go-back-api";
import { useStateManager } from "~/modules/StateManager.hooks";
import { createServerStateManager, ServerStateManager } from "~/modules/StateManager.server";
import { useRootLoaderData } from "~/root";
import type { InviteIndexLoaderData } from "~/routes/$enterprise+/plugin+/$sessionId.appointment.invite";
import { getInfraConfiguration } from "~/services/infra-configuration.server";
import { getOffice } from "~/services/schedulingRules.server";
import { getEmployee, getSubject } from "~/services/schedulingRules.server";
import { useNonce } from "~/utils/nonce-provider";
import { parseEmployeeId } from "~/utils/utils";
import { errors } from "~/utils/validators.utils";

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://www.google.com" },
  { rel: "preconnect", href: "https://www.gstatic.com", crossOrigin: "anonymous" },
];

export interface ScheduleLayoutLoaderData extends SerializeFrom<typeof loader> {}

export function useScheduleLayoutData() {
  return useRouteLoaderData<typeof loader>("routes/$enterprise+/plugin+/$sessionId.schedule");
}

export async function loader(args: LoaderFunctionArgs) {
  return args.context.measurer.time(
    "$enterprise_schedule",
    async function $enterpriseScheduleLoader() {
      const stateManager = await createServerStateManager(args);

      if (stateManager.state.appointmentId) {
        // If our current session has an appointmentId, we disallow it accessing previous schedule steps.
        // And restart the plugin flow.

        return stateManager.redirectToNewSession();
      }

      const data = await resolveScheduleParams(stateManager);

      return json(data);
    },
  );
}

export default function ScheduleLayout() {
  return (
    <GoBackProvider value="schedule">
      <Layout />
    </GoBackProvider>
  );
}

function Layout() {
  const { info } = useLoaderData<typeof loader>();

  useRecaptchaScript();
  useActiveStepMessage({ info });
  useBrowserNavigationInterceptor();

  return (
    <Summary info={info}>
      <StepOutlet />
    </Summary>
  );
}

export function ErrorBoundary() {
  return <AppErrorBoundary />;
}

export type SummaryProps = (ScheduleLayoutLoaderData | InviteIndexLoaderData) & {
  hideLockedSteps?: boolean;
  children: ReactNode;
};

export function Summary({ children, ...props }: SummaryProps) {
  return (
    <>
      <Media data-component="Summary" data-variant="Desktop" showFor="desktop">
        <div className="mx-auto grid max-w-[100rem] grid-cols-3 gap-lg">
          <div className="max-w-md py-3xs">
            <DesktopSummaryContainer {...props} />
          </div>
          <div className="col-span-2 grid grid-rows-[auto_1fr] gap-md" data-component="Step">
            <ContentTitle />
            {children}
          </div>
        </div>
      </Media>
      <Media data-component="Summary" data-variant="Mobile" showFor="mobile">
        <div className="grid gap-sm" data-component="Step">
          <MobileSummaryContainer {...props} />
          {children}
        </div>
      </Media>
    </>
  );
}

export function useRecaptchaScript() {
  const nonce = useNonce();
  const managerLocale = useStateManager().state.config.language;
  const rootLocale = useRootLoaderData()?.locale;

  const locale = managerLocale ?? rootLocale;

  useEffect(() => {
    injectScript(locale, nonce);
  }, [nonce, locale]);
}

async function resolveScheduleParams(stateManager: ServerStateManager) {
  const [employee, meetingType, office, subject] = await Promise.all([
    resolveEmployee(stateManager),
    resolveMeetingType(stateManager),
    resolveOffice(stateManager),
    resolveSubject(stateManager),
  ]);

  const { EXTERNAL_API_URL } = getInfraConfiguration(stateManager.enterprise);

  const info = {
    employee: employee
      ? {
          id: employee.id.toString(),
          externalId: employee.external_id,
          firstName: employee.user.first_name,
          lastName: employee.user.last_name,
          phoneNumber: employee.user.phone_number,
          function: employee.roles?.contact?.function ?? null,
          avatar:
            employee.profile_picture == null
              ? null
              : `${EXTERNAL_API_URL}/pictures/${employee?.profile_picture}`,
        }
      : null,
    meetingType: meetingType ?? null,
    office: office
      ? {
          id: office.id.toString(),
          externalId: office.external_id,
          title: office.title,
          isVirtual: office.is_virtual,
          timeZone: office?.timezone,
          address: office.location?.full_address ?? null,
        }
      : null,
    subject: subject
      ? { id: subject.id.toString(), externalId: subject.external_id, title: subject.title }
      : null,
  };

  return { info };
}

async function resolveOffice(stateManager: ServerStateManager) {
  const { officeId } = stateManager.state;
  if (!officeId) return;

  const office = await getOffice(officeId, stateManager.getRequestConfig());
  if (office) return office;

  stateManager.throw(
    errors.notFound.create({
      eventType: PluginMisconfigured.OFFICE_ID,
      translation: {
        key: "errors.configuration.invalidOffice.label",
        clearKey: "errors.configuration.invalidOffice.clear",
        interpolations: {
          identifier: officeId,
        },
      },
    }),
  );
}

async function resolveSubject(stateManager: ServerStateManager) {
  const { subjectId } = stateManager.state;
  if (!subjectId) return;

  const subject = await getSubject(subjectId, stateManager.getRequestConfig());
  if (subject) return subject;

  stateManager.throw(
    errors.notFound.create({
      eventType: PluginMisconfigured.SUBJECT_ID,
      translation: {
        key: "errors.configuration.invalidSubject.label",
        clearKey: "errors.configuration.invalidSubject.clear",
        interpolations: {
          identifier: subjectId,
        },
      },
    }),
  );
}

async function resolveMeetingType(stateManager: ServerStateManager) {
  const { meetingType } = stateManager.state;
  if (!meetingType) return;

  return meetingType;
}

async function resolveEmployee(stateManager: ServerStateManager) {
  const employeeId = parseEmployeeId(stateManager.state.employeeId);
  if (!employeeId) return;

  const employee = await getEmployee(employeeId, stateManager.getRequestConfig());
  if (employee) return employee;

  stateManager.throw(
    errors.notFound.create({
      eventType: PluginMisconfigured.EMPLOYEE_ID,
      translation: {
        key: "errors.configuration.invalidEmployee.label",
        clearKey: "errors.configuration.invalidEmployee.clear",
        interpolations: {
          identifier: employeeId,
        },
      },
    }),
  );
}

export function useActiveStepMessage({ info }: Pick<SummaryProps, "info">) {
  const stateManager = useStateManager();
  const { activeStep } = useActiveStep();

  const timetable = stateManager.state.timetable;

  const _payload = {
    subject: info.subject ? { id: info.subject.id, title: info.subject.title } : undefined,
    office: info.office ? { id: info.office.id, title: info.office.title } : undefined,
    meetingType: info.meetingType ?? undefined,
    employee: info.employee
      ? {
          id: info.employee.id,
          lastName: info.employee.lastName,
          firstName: info.employee.firstName,
        }
      : undefined,
    timetable:
      timetable?.start && timetable.end
        ? { start: timetable.start, end: timetable.end }
        : undefined,
  };

  const payload = useRef(_payload);
  payload.current = _payload;

  useEffect(() => {
    if (activeStep?.id) {
      sendMessage({ type: "STEP_SHOWN", payload: { step: activeStep.id, ...payload.current } });
    }
  }, [activeStep?.id]);
}

function injectScript(locale?: string, nonce?: string) {
  const scriptId = "pexip-engage-recaptcha";
  if (document.querySelector(`#${scriptId}`)) {
    return; // Avoid duplicate script injection
  }

  const searchParams = new URLSearchParams();
  searchParams.set("render", "explicit");
  locale && searchParams.set("hl", locale);

  const script = document.createElement("script");
  script.id = scriptId;
  script.src = `https://www.google.com/recaptcha/api.js?${searchParams}`;
  script.type = "text/javascript";
  script.async = true;
  script.defer = true;
  script.nonce = nonce;

  document.body.appendChild(script);
}
